/**
 * The pure reducer that assembles a detached run's SSE events into an ordered,
 * renderable item list — the heart of "the stream is a VIEW onto the run". Feed
 * it {@link ChatEvent}s (buffered replay first, then the live tail — identical
 * code path, so ordering is preserved across a reattach) and it produces a
 * {@link StreamState}: interleaved text / tool / media / directive items plus the
 * run's status. Immutable + deterministic (ids come from a monotonic counter in
 * state, never a clock/RNG), so it is exhaustively unit-tested with no DOM.
 */
import type { UiDirective } from '@4f/shared';
import {
  extractDirective,
  extractMediaRef,
  isInputDirective,
  type MediaRef,
} from '../generative-ui/directive.ts';
import type { ChatEvent } from './run-events.ts';

/** One renderable unit in the stream, in arrival order. */
export type StreamItem =
  | { readonly kind: 'text'; readonly id: string; readonly text: string }
  | {
      readonly kind: 'tool';
      readonly id: string;
      readonly toolName: string;
      readonly status: 'running' | 'done';
      readonly output?: unknown;
    }
  | {
      readonly kind: 'media';
      readonly id: string;
      readonly ref: MediaRef;
      /**
       * A resolved asset URL, once known. Absent while the placeholder shows.
       * Set by the client-only FAKED reveal (prebuilt pre-generated asset) or,
       * for a real job, by the WS relay swap (PR8 F6) — the two never mix.
       */
      readonly src?: string;
      /**
       * True when this media is a client-only FAKED reveal of a prebuilt
       * pre-generated asset (a `setTimeout` beat, `FakedMedia`), NOT a real job
       * awaiting the WS swap. Defaults to absent for agent-produced media.
       */
      readonly faked?: boolean;
    }
  | {
      readonly kind: 'directive';
      readonly id: string;
      readonly directive: UiDirective;
      /** True once the user answered a `form`/`choice`/`file` (or it never asked). */
      readonly answered: boolean;
    };

/**
 * The run's lifecycle as the client sees it. `canceled` is terminal like `error`
 * but is NOT a failure — the user asked for it, nothing was stored and nothing was
 * billed — so it must never render the error line's "tap to retry".
 */
export type StreamStatus =
  | 'idle'
  | 'streaming'
  | 'waiting_input'
  | 'finished'
  | 'canceled'
  | 'error';

/** The accumulated view of one run's channel. */
export interface StreamState {
  readonly runId: string | null;
  readonly conversationId: string | null;
  readonly girlfriendId: string | null;
  readonly items: readonly StreamItem[];
  readonly messageId: string | null;
  readonly error: string | null;
  /**
   * The server's stable code from the terminal `run-error` frame, when it sent
   * one. Kept alongside `error` because the MESSAGE alone cannot tell a gate
   * `402` from an upstream fault, and a mid-run `quota_exceeded` has to reach the
   * paywall rather than a "Something went wrong / Retry" whose retry re-spends.
   * `null` for a frame from a deploy that predates the field (see `run-events.ts`).
   */
  readonly errorCode: string | null;
  /** The terminal event seen, if any — drives `status` alongside pending input. */
  readonly terminal: 'finished' | 'canceled' | 'error' | null;
  /** Monotonic id source — makes item ids deterministic (test-friendly). */
  readonly seq: number;
  readonly status: StreamStatus;
}

/** A pristine, empty stream — the store's starting point for a fresh send or reattach. */
export const initialStreamState: StreamState = {
  runId: null,
  conversationId: null,
  girlfriendId: null,
  items: [],
  messageId: null,
  error: null,
  errorCode: null,
  terminal: null,
  seq: 0,
  status: 'idle',
};

/**
 * Mark the turn FAILED without a run event — the state a send refused BEFORE the
 * stream opened lands in.
 *
 * A run that dies mid-flight ends on `run-error`, which the reducer already folds into
 * `status: 'error'`. A turn the edge refuses up front never produces one: it answers a
 * JSON envelope instead of SSE (`409 conversation_busy` — her reply to the previous
 * turn is still running; `422 content_blocked`; a `5xx`), so the stream stayed `idle`
 * and the view had nothing to render. The optimistic bubble sat there with no
 * explanation and no control — the send simply appeared to do nothing.
 *
 * Deriving that from "there is an error somewhere" instead is what this exists to
 * avoid: `error` is also set by a failed history reload AFTER a turn completed
 * successfully, and by a failed form ack MID-stream. Both would then offer a retry
 * that re-sends an answered turn (a duplicate row and a second credit) or paint an
 * alert over a healthy reply. The turn's own outcome is the only honest signal.
 */
export function markStreamFailed(state: StreamState, message: string): StreamState {
  return { ...state, terminal: 'error', error: message, status: 'error' };
}

/** True when an unanswered input directive is parking the run. */
function hasPendingInput(items: readonly StreamItem[]): boolean {
  return items.some(
    (item) => item.kind === 'directive' && isInputDirective(item.directive) && !item.answered,
  );
}

/** Derive the display status from the terminal event, pending input, and whether a run is live. */
function deriveStatus(state: Omit<StreamState, 'status'>): StreamStatus {
  if (state.terminal === 'error') {
    return 'error';
  }
  // A stop wins over a parked form: the run it was waiting on is gone, so leaving
  // the view in `waiting_input` would park the composer on a form nothing can answer.
  if (state.terminal === 'canceled') {
    return 'canceled';
  }
  if (hasPendingInput(state.items)) {
    return 'waiting_input';
  }
  if (state.terminal === 'finished') {
    return 'finished';
  }
  return state.runId ? 'streaming' : 'idle';
}

/** Recompute `status` after any items/terminal change (single source of truth). */
function withStatus(state: Omit<StreamState, 'status'>): StreamState {
  return { ...state, status: deriveStatus(state) };
}

/** Append a delta to the trailing text item, or start a new one when the last item is not text. */
function appendText(state: StreamState, text: string): StreamState {
  const last = state.items[state.items.length - 1];
  if (last && last.kind === 'text') {
    const items = state.items.slice(0, -1).concat({ ...last, text: last.text + text });
    return withStatus({ ...state, items });
  }
  const id = String(state.seq);
  const items = state.items.concat({ kind: 'text', id, text });
  return withStatus({ ...state, items, seq: state.seq + 1 });
}

/**
 * Mark one still-running tool item done: the most recent running tool with a
 * matching name, else (no name match) the most recent running tool. A no-op when
 * nothing is running — a result without a preceding call just yields no tool row.
 */
function settleTool(
  items: readonly StreamItem[],
  toolName: string,
  output?: unknown,
): StreamItem[] {
  let target = -1;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item && item.kind === 'tool' && item.status === 'running') {
      if (item.toolName === toolName) {
        target = i;
        break;
      }
      if (target === -1) {
        target = i;
      }
    }
  }
  if (target === -1) {
    return items.slice();
  }
  const next = items.slice();
  const item = next[target];
  if (item && item.kind === 'tool') {
    next[target] = { ...item, status: 'done', ...(output !== undefined ? { output } : {}) };
  }
  return next;
}

/** Fold one tool result into items: a directive, an inline media ref, or a plain tool completion. */
function applyToolResult(state: StreamState, toolName: string, output: unknown): StreamState {
  const directive = extractDirective(output);
  if (directive) {
    const settled = settleTool(state.items, toolName);
    const id = String(state.seq);
    const items = settled.concat({ kind: 'directive', id, directive, answered: false });
    return withStatus({ ...state, items, seq: state.seq + 1 });
  }

  const media = extractMediaRef(output);
  if (media) {
    const settled = settleTool(state.items, toolName);
    const id = String(state.seq);
    const items = settled.concat({ kind: 'media', id, ref: media });
    return withStatus({ ...state, items, seq: state.seq + 1 });
  }

  return withStatus({ ...state, items: settleTool(state.items, toolName, output) });
}

/**
 * Fold one run event into the view. Unknown-to-us fields are ignored; the union
 * is switched exhaustively, so a new {@link ChatEvent} member is a compile error
 * until handled here.
 */
export function applyChatEvent(state: StreamState, event: ChatEvent): StreamState {
  switch (event.type) {
    case 'run-started':
      return withStatus({
        ...state,
        runId: event.runId,
        conversationId: event.conversationId,
        girlfriendId: event.girlfriendId,
        terminal: null,
        error: null,
        // Cleared with `error`, and for a sharper reason: a stale `quota_exceeded`
        // left here would make the NEXT run — a perfectly healthy one — render the
        // paywall over its reply, because `ChatStream` reads this code directly.
        errorCode: null,
      });
    case 'text-delta':
      return appendText(state, event.text);
    case 'tool-call': {
      const id = String(state.seq);
      const items = state.items.concat({
        kind: 'tool',
        id,
        toolName: event.toolName,
        status: 'running',
      });
      return withStatus({ ...state, items, seq: state.seq + 1 });
    }
    case 'tool-result':
      return applyToolResult(state, event.toolName, event.output);
    case 'run-finished':
      return withStatus({ ...state, terminal: 'finished', messageId: event.messageId });
    // Terminal, no `error`: whatever streamed before the stop stays on screen (the
    // server persisted none of it), and the composer unlocks for the next turn.
    case 'run-canceled':
      return withStatus({ ...state, terminal: 'canceled' });
    case 'run-error':
      return withStatus({
        ...state,
        terminal: 'error',
        error: event.message,
        errorCode: event.code ?? null,
      });
  }
  // Every `ChatEvent` member is handled above; a new one makes this reachable
  // and TS flags the missing return (exhaustiveness without an unused binding).
  return state;
}

/** Fold a whole event sequence (the replay-then-tail entry point). */
export function reduceChatEvents(
  events: Iterable<ChatEvent>,
  from: StreamState = initialStreamState,
): StreamState {
  let state = from;
  for (const event of events) {
    state = applyChatEvent(state, event);
  }
  return state;
}

/**
 * The subset of a resolved media asset the completion swap needs — matches
 * `MediaAssetView` (`api/types.ts`), kept minimal + local so this pure reducer
 * module stays decoupled from the api client's schemas.
 */
export interface ResolvableAsset {
  /** The job that produced it — `null` for a pre-generated prebuilt asset (never matches a live placeholder). */
  readonly jobId: string | null;
  /** A ready-to-fetch URL, or `null` when unsignable (the item stays a placeholder). */
  readonly url: string | null;
}

/** How a resolved media item is revealed. */
export interface ResolveMediaOptions {
  /**
   * Reveal through the client-side FAKED "generating…" beat instead of popping
   * the finished asset in instantly. Set for a PREBUILT (free front door)
   * companion, whose assets were pre-generated offline: without the beat her
   * media appears the instant the turn lands, which reads as canned rather than
   * made-for-you and undercuts the whole free-start illusion (memory
   * `monetization-onboarding-funnel`). Purely presentational — it changes WHEN
   * the already-resolved URL is shown, never whether a job ran or what was
   * billed.
   */
  readonly faked?: boolean;
}

/**
 * The WS-relay completion swap (PR8 F6): replace each still-pending media item's
 * placeholder with its finished asset's URL. Matches `item.ref.jobId` against
 * `asset.jobId` (the documented job↔asset link — `MediaAssetView.jobId` "lets the
 * client match a live inline placeholder to its asset"). An item that already
 * carries a `src` (a FAKED reveal, or an earlier resolve) is left untouched, as is
 * one with no matching or not-yet-signable asset (still generating). Pure and
 * identity-preserving when nothing changed, so a caller can skip a re-render.
 */
export function resolveMediaAssets(
  state: StreamState,
  assets: readonly ResolvableAsset[],
  options: ResolveMediaOptions = {},
): StreamState {
  let changed = false;
  const items = state.items.map((item) => {
    if (item.kind !== 'media' || item.src !== undefined || item.faked) {
      return item;
    }
    const asset = assets.find((a) => a.jobId !== null && a.jobId === item.ref.jobId);
    if (!asset || asset.url === null) {
      return item;
    }
    changed = true;
    return options.faked ? { ...item, src: asset.url, faked: true } : { ...item, src: asset.url };
  });
  return changed ? { ...state, items } : state;
}

/**
 * The job ids of every media placeholder still waiting for its asset — the input
 * to the POLL reconciliation that backs up the WS `media_job` push. The push is
 * the fast path, but it is the ONLY path today: a backgrounded tab (mobile Safari
 * kills the socket) never receives it, so the shimmer spins forever even though
 * the asset landed in e2 minutes ago. De-duplicated, so N placeholders on one job
 * cost one status read.
 */
export function pendingMediaJobIds(state: StreamState): readonly string[] {
  const ids = new Set<string>();
  for (const item of state.items) {
    if (item.kind === 'media' && item.src === undefined && item.ref.jobId) {
      ids.add(item.ref.jobId);
    }
  }
  return [...ids];
}

/** Mark a directive item answered (after a successful `submitInput`) and recompute status. */
export function markDirectiveAnswered(state: StreamState, directiveId: string): StreamState {
  let changed = false;
  const items = state.items.map((item) => {
    if (item.kind === 'directive' && item.id === directiveId && !item.answered) {
      changed = true;
      return { ...item, answered: true };
    }
    return item;
  });
  return changed ? withStatus({ ...state, items }) : state;
}
